/**
 * src/components/Financial/modules/ComplexAspectModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一】
 */
import React from "react";
import { Star, Info } from "lucide-react";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";
import * as Astro from "../../../astronomy.js";

/**
 * 複合アスペクト（構成）の計算ロジック
 */
export const calculateComplexAspects = function (
  timeline,
  isHeliocentric,
  aspectSettings,
) {
  var periods = [];
  if (!timeline || timeline.length === 0) return periods;

  var ALLOWED_GEOMETRY = [
    "Grand Trine",
    "T-Square",
    "Yod",
    "Grand Cross",
    "Mystic Rectangle",
    "Kite",
    "Thor's Hammer",
    "Finger of God",
  ];

  var planets = isHeliocentric
    ? [
        "Earth",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
      ]
    : [
        "Sun",
        "Moon",
        "Mercury",
        "Venus",
        "Mars",
        "Jupiter",
        "Saturn",
        "Uranus",
        "Neptune",
        "Pluto",
      ];

  var activePatterns = {};

  for (var i = 0; i < timeline.length; i++) {
    var row = timeline[i];
    if (row.isFuture && !row.timestamp) continue;

    var date = row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
    var positions = {};
    planets.forEach(function (p) {
      positions[p] = isHeliocentric
        ? Astro.getHeliocentricLongitude(p, date)
        : Astro.getPlanetLongitude(p, date);
    });

    var aspects = Astro.getAllAspects(positions, "major");
    var patterns = Astro.detectSpecialPatterns(aspects, positions);

    var foundKeys = new Set();

    if (patterns && patterns.length > 0) {
      patterns.forEach(function (p) {
        var isGeometry = ALLOWED_GEOMETRY.some(function (name) {
          return p.name.indexOf(name) !== -1;
        });

        if (isGeometry && p.planets && p.planets.length >= 3) {
          var key = p.name + ":" + p.planets.slice().sort().join("-");
          foundKeys.add(key);

          if (!activePatterns[key]) {
            activePatterns[key] = {
              start: i,
              name: p.name,
              planets: p.planets,
              type: p.type,
              desc: p.desc,
            };
          }
        }
      });
    }

    Object.keys(activePatterns).forEach(function (key) {
      if (!foundKeys.has(key)) {
        var p = activePatterns[key];
        periods.push({
          startIndex: p.start,
          endIndex: i - 1,
          name: p.name,
          planets: p.planets,
          type: p.type,
          desc: p.desc,
        });
        delete activePatterns[key];
      }
    });
  }

  Object.keys(activePatterns).forEach(function (key) {
    var p = activePatterns[key];
    periods.push({
      startIndex: p.start,
      endIndex: timeline.length - 1,
      name: p.name,
      planets: p.planets,
      type: p.type,
      desc: p.desc,
    });
  });

  return periods;
};

/**
 * 描画レイヤー
 */
export const ComplexAspectLayer = function (props) {
  var show = props.show;
  var hits = props.hits || [];
  var coords = props.coords;
  var displayRange = props.displayRange;
  var isDarkMode = props.isDarkMode; // ★ダークモード判定を受け取る

  if (!show) return null;

  var lanes = [];
  var visibleHits = hits
    .filter(function (h) {
      return !(
        h.endIndex < displayRange.start ||
        h.startIndex >= displayRange.start + displayRange.count
      );
    })
    .sort(function (a, b) {
      return a.startIndex - b.startIndex;
    });

  return (
    <g className="complex-aspect-layer">
      {visibleHits.map(function (period, i) {
        var startIdx = Math.max(period.startIndex, displayRange.start);
        var endIdx = Math.min(
          period.endIndex,
          displayRange.start + displayRange.count - 1,
        );

        var x1 = coords.getX(startIdx - displayRange.start);
        var x2 = coords.getX(endIdx - displayRange.start);
        var width = Math.max(2, x2 - x1);

        var laneIndex = 0;
        while (
          laneIndex < lanes.length &&
          lanes[laneIndex] > period.startIndex
        ) {
          laneIndex++;
        }
        lanes[laneIndex] =
          period.endIndex + Math.floor(displayRange.count * 0.1);

        var color =
          period.type === "benefit"
            ? "#16a34a"
            : period.type === "hard"
              ? "#dc2626"
              : "#8b5cf6";

        var verticalOffset = laneIndex * -35;

        return (
          <g key={"complex-period-" + i}>
            {/* 期間の背景塗り：ダークモード時は透明度を調整 */}
            <rect
              x={x1}
              y={coords.pad.t}
              width={width}
              height={coords.iH}
              fill={color}
              opacity={isDarkMode ? "0.12" : "0.08"}
            />

            <line
              x1={x1}
              y1={coords.pad.t}
              x2={x2}
              y2={coords.pad.t}
              stroke={color}
              strokeWidth="2"
              opacity="0.6"
            />

            {(function () {
              var labelX = x1 + width / 2;
              var labelY = coords.pad.t - 0 + verticalOffset;

              return (
                <g transform={"translate(" + labelX + ", " + labelY + ")"}>
                  {/* アスペクト名：背景色に合わせた縁取りを追加 */}
                  <text
                    y="0"
                    textAnchor="middle"
                    fontSize="10"
                    fill={color}
                    fontWeight="900"
                    style={{
                      paintOrder: "stroke",
                      stroke: isDarkMode ? "#0b0e14" : "#fff",
                      strokeWidth: "3px",
                      strokeLinejoin: "round",
                    }}
                  >
                    {period.name.toUpperCase()}
                  </text>

                  {/* 惑星アイコン */}
                  <g transform="translate(0, 18)">
                    {period.planets.map(function (p, pIdx) {
                      return (
                        <text
                          key={pIdx}
                          x={(pIdx - (period.planets.length - 1) / 2) * 14}
                          fontSize="12"
                          textAnchor="middle"
                          fill={
                            PLANET_COLORS[p] ||
                            (isDarkMode ? "#ccc" : "#64748b")
                          }
                          fontWeight="bold"
                          style={{
                            paintOrder: "stroke",
                            stroke: isDarkMode ? "#0b0e14" : "#fff",
                            strokeWidth: "2.5px",
                          }}
                        >
                          {PLANET_SYMBOLS[p]}
                        </text>
                      );
                    })}
                  </g>
                </g>
              );
            })()}
          </g>
        );
      })}
    </g>
  );
};

/**
 * 設定UI
 */
export const ComplexAspectSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var isDarkMode = props.isDarkMode;

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
  };

  /**
   * ボタン共通スタイル (f98.jpg に合わせた統一デザイン)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Star size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>複合アスペクト (Planetary Geometry)</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          marginTop: "12px",
          fontSize: "12px",
          color: theme.subText,
          lineHeight: "1.5",
        }}
      >
        <div>幾何学的アスペクトの形成期間をチャート上に表示します。</div>
      </div>
    </div>
  );
};
